import { createContext, useCallback, useContext, useMemo, useState } from 'react';

import { formatCentsToDollarString } from '@fhs/utils';
import { useToast } from '@fhs-legacy/frontend/src/hooks/use-toast';
import { IGiftCard } from '@fhs-legacy/frontend/src/state/payment/types';

import { useUserAccountsQuery } from '../queries/use-user-accounts';

export type GiftCardContext = {
  formattedBalance: string;
  handleBalanceUpdate: () => void;
  handleError: () => void;
  currentBalance: number | undefined;
  isBalanceLoading: boolean;
  lastDigits: string;
  hasGiftCard: boolean;
  refetchGiftCardAccount: () => Promise<IGiftCard | null>;
};

const giftCardContext = createContext<null | GiftCardContext>(null);

export type GiftCardProviderProps = {
  children?: React.ReactNode;
  /** When cart preload already loaded userAccounts, skip fetch on checkout. */
  skipFetch?: boolean;
  preloadedGiftCard?: IGiftCard | null;
};

export const GiftCardProvider = (props: GiftCardProviderProps) => {
  const { skipFetch = false, preloadedGiftCard } = props;
  const { data: userAccountsData, isFetching, refetch } = useUserAccountsQuery({
    enabled: !skipFetch,
  });
  const giftCardFromQuery = userAccountsData?.accounts?.find(account => account?.giftCard)?.giftCard;
  // After cart preload, use preloaded data until a refetch lands; then prefer fresh query cache.
  const giftCard = giftCardFromQuery ?? preloadedGiftCard ?? undefined;

  const [isRefetching, setIsRefetching] = useState(false);

  const currentBalance = giftCard?.currentBalance;

  const formattedBalance = useMemo(
    () => (currentBalance !== undefined ? `$${formatCentsToDollarString(currentBalance)}` : ''),
    [currentBalance]
  );

  const { show } = useToast();

  const refetchGiftCardAccount = useCallback(async () => {
    setIsRefetching(true);
    try {
      const { data: updatedUserAccountsData } = await refetch();
      return updatedUserAccountsData?.accounts?.find(account => account?.giftCard)?.giftCard ?? null;
    } catch (_error) {
      return null;
    } finally {
      setIsRefetching(false);
    }
  }, [refetch]);

  const handleBalanceUpdate = useCallback(() => {
    void refetchGiftCardAccount();
    show({
      variant: 'positive',
      text: 'Digital gift card funds updated!',
    });
  }, [refetchGiftCardAccount, show]);

  const handleError = useCallback(() => {
    show({
      variant: 'negative',
      text: 'Error updating digital gift card funds',
    });
  }, [show]);

  return (
    <giftCardContext.Provider
      value={{
        formattedBalance,
        handleBalanceUpdate,
        handleError,
        currentBalance,
        isBalanceLoading: isFetching || isRefetching,
        lastDigits: giftCard?.last4 ?? '',
        hasGiftCard: Boolean(giftCard),
        refetchGiftCardAccount,
      }}
    >
      {props.children}
    </giftCardContext.Provider>
  );
};

export function useGiftCardContext() {
  const context = useContext(giftCardContext);

  if (!context) {
    throw new Error('Must consume `useGiftCardContext` with a GiftCardProvider');
  }

  return context;
}
